import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { JwtHelperService } from "@auth0/angular-jwt";

import { ILogin, IUser } from "../interfaces/ilogin";
import { IBusinessFunction } from "../interfaces/ibusinessfunction";
import { AuthService } from "../services/auth.service";
import { ApplicationSettingsService } from "./applicationsettings.service";

/**
 * @WhatItDoes A service for logging the user in and setting the user object being used throughout the application.
 * Also sets the business function for the company if only one is present for the user
 *
 * @HowToUseIt Inject the service
 */
@Injectable()
export class LoginService {
    constructor(private http: HttpClient,
        private authService: AuthService,
        private toastr: ToastrService,
        private applicationSettingsService: ApplicationSettingsService,
        private jwtHelper: JwtHelperService) {

    }

    /**
     * Logs the user in based on the details provided
     * @param loginDetails contains the username and password for the user
     */
    login(loginDetails: ILogin): Observable<any> {
        const headers = new HttpHeaders({ "Content-Type": "application/x-www-form-urlencoded" });
        const body: string = `username=${loginDetails.username}&password=${loginDetails.password}&businessfunctionid=${loginDetails.businessFunctionId}`;
        return this.http.post(`${this.applicationSettingsService.apiUrl.replace("/api", "")}token`, body, { headers: headers })
            .pipe(
                map((res: any) => {
                    const user: IUser = res as IUser;
                    const decodedAccessToken: any = this.jwtHelper.decodeToken(user.access_token);

                    user.username = loginDetails.username;
                    user.firstName = decodedAccessToken.given_name;
                    user.lastName = decodedAccessToken.family_name;
                    user.businessFunctions = new Array();
                    // create business functions the user has access too
                    if (typeof (decodedAccessToken.business_function) === "string") {
                        this._addBusinessFunctionToUser(decodedAccessToken.business_function, user);
                    } else {
                        decodedAccessToken.business_function.forEach((businessFunction: string) => {
                            this._addBusinessFunctionToUser(businessFunction, user);
                        });
                    }

                    // set user roles
                    user.roles = decodedAccessToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
                    this.authService.manualLogin = true;

                    this.authService.user = user;
                    return user;
                }),
                catchError((error: any) => { return this._handleError(error); } )
            );
    }

    /**
     * Adds the business function to the user object
     * @param businessFunction business function to add to the user
     * @param user user to add the business function too
     */
    private _addBusinessFunctionToUser(businessFunction: string, user: IUser): void {
        let bf: any = JSON.parse(businessFunction);
        user.businessFunctions.push({ id: bf.Id, displayName: bf.DisplayName, defaultRoute: bf.DefaultRoute } as IBusinessFunction);
    }

    private _handleError(error: any): Observable<Error> {
        // in a real world app, we might use a remote logging infrastructure
        if (error.status === 401 || error.status === 403) {
            this.toastr.error(error.error);
            this.authService.user = null;
        } else {
            if (error.status === 404 || error.status === 400) {
                this.toastr.error(error.error);
            }
        }
        return throwError(error);
    }
}